import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api, STAGE_LABELS } from "../lib/api";
import { useGameSocket } from "../lib/useGameSocket";
import { BingoCard, CurrentBall, completeRows } from "../components/BingoBits";
import { LiveStream } from "../components/LiveStream";
import { Button } from "../components/ui/button";
import { toast, Toaster } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { fireConfetti, playFanfare } from "../lib/celebrate";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { REACTION_ICONS } from "../components/StudioFx";
import { PayoutForm } from "../components/PayoutForm";

function countMarked(card, drawn) {
  const set = new Set(drawn);
  let m = 0, needed = 0;
  card.forEach(row => row.forEach(n => { if (n !== null) { needed++; if (set.has(n)) m++; } }));
  return { marked: m, needed };
}
function cardCanClaim(card, drawn, stage) {
  const rc = completeRows(card, drawn).length;
  if (stage === "row") return rc >= 1;
  if (stage === "two_rows") return rc >= 2;
  if (stage === "full") return rc === 3;
  return false;
}

export default function ViewerPlay() {
  const { ticketId } = useParams();
  const [tickets, setTickets] = useState(null);
  const [idx, setIdx] = useState(0);
  const [claiming, setClaiming] = useState(false);
  const [showWin, setShowWin] = useState(null);
  const [lastReact, setLastReact] = useState(0);
  const [burst, setBurst] = useState(null);

  const sendReaction = (kind) => {
    const now = Date.now();
    if (now - lastReact < 700 || !ticket) return;
    setLastReact(now);
    setBurst(kind);
    setTimeout(() => setBurst(null), 500);
    api.post(`/games/${ticket.game_id}/react`, { kind }).catch(() => {});
  };

  useEffect(() => {
    api.get(`/tickets/${ticketId}`).then(async (r) => {
      const main = r.data;
      const saved = JSON.parse(localStorage.getItem("my_tickets") || "[]").filter((id) => id !== ticketId);
      let others = [];
      if (saved.length) {
        try {
          const b = await api.get(`/tickets/batch?ids=${saved.join(",")}`);
          others = b.data.filter((t) => t.game_id === main.game_id && t.paid);
        } catch (e) { /* ignore */ }
      }
      setTickets([main, ...others]);
    }).catch(() => setTickets([]));
  }, [ticketId]);

  const ticket = tickets?.[0];
  const { game, connected, polling } = useGameSocket(ticket?.game_id, (msg) => {
    if (msg.type === "claim_verified") {
      if (msg.approve) {
        const mine = !!tickets?.some((t) => t.id === msg.ticket_id);
        setShowWin({ name: msg.display_name, stage: msg.stage, mine });
        if (mine) { fireConfetti(5000); playFanfare(); }
        setTimeout(() => setShowWin(null), mine ? 3000 : 6000);
      }
    }
  });

  const drawn = game?.drawn || [];
  const stage = game?.stage;
  const current = tickets?.[idx];

  const myWins = useMemo(() => {
    if (!game || !tickets) return [];
    const ids = new Set(tickets.map((t) => t.id));
    return ["row", "two_rows", "full"].map((s) => {
      const w = game.winners?.[s];
      return w && ids.has(w.ticket_id) && !w.has_iban ? { ...w, stage: s, prize: game.prizes[s] } : null;
    }).filter(Boolean);
  }, [game, tickets]);
  const [payoutDone, setPayoutDone] = useState([]);
  const pendingWin = myWins.find((w) => !payoutDone.includes(w.claim_id));

  const claimable = useMemo(() => {
    if (!tickets || !game || game.status !== "live" || game.pending_claim_id) return [];
    return tickets.map((t, i) => (cardCanClaim(t.card, drawn, stage) ? i : -1)).filter((i) => i >= 0);
  }, [tickets, game, stage, drawn]);
  const canClaim = claimable.includes(idx);

  useEffect(() => {
    if (claimable.length && !claimable.includes(idx)) setIdx(claimable[0]);
     
  }, [claimable.join(",")]);

  const claim = async () => {
    setClaiming(true);
    try {
      await api.post("/claims", { ticket_id: current.id, stage });
      toast.success("BINGO! Wacht op verificatie...");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Claim mislukt");
    } finally {
      setClaiming(false);
    }
  };

  if (tickets === null) return <div className="min-h-screen bg-[#0E1118] text-slate-300 flex items-center justify-center">Laden...</div>;
  if (!ticket) return <div className="min-h-screen bg-[#0E1118] text-slate-300 flex items-center justify-center">Kaart niet gevonden</div>;
  if (!ticket.paid) {
    return <div className="min-h-screen bg-[#0E1118] text-slate-100 flex items-center justify-center p-6 text-center">
      <div>
        <h1 className="text-2xl font-black text-[#FF1744]">Kaart nog niet betaald</h1>
        <p className="mt-3 text-slate-400">Ga terug om de betaling te voltooien.</p>
      </div>
    </div>;
  }

  const last = drawn.length > 0 ? drawn[drawn.length - 1] : null;
  const stats = countMarked(current.card, drawn);
  const rc = completeRows(current.card, drawn).length;

  if (game?.status === "finished") {
    return <div className="min-h-screen bg-[#0E1118] text-slate-100 flex items-center justify-center p-6 text-center">
      <Toaster theme="dark" position="top-center" richColors />
      <div data-testid="game-finished" className="w-full max-w-md space-y-6">
        <div>
          <h1 className="text-2xl font-black text-[#FFD700]" style={{ fontFamily: "Unbounded, sans-serif" }}>Dit spel is afgelopen</h1>
          <p className="mt-3 text-slate-400">Deze kaart hoort bij <b className="text-slate-200">{game.title}</b>, dat al is beëindigd.</p>
        </div>
        {pendingWin && <div className="text-left"><PayoutForm win={pendingWin} defaultName={ticket.display_name} onDone={() => setPayoutDone((d) => [...d, pendingWin.claim_id])} /></div>}
        <a href="/viewer" data-testid="back-to-viewer" className="inline-block px-5 py-3 rounded-xl bg-[#FFD700] text-[#0B0C10] font-black">Naar mijn kaarten →</a>
      </div>
    </div>;
  }

  return (
    <div className="min-h-screen bg-[#0E1118] text-slate-100 pb-44">
      <Toaster theme="dark" position="top-center" richColors />
      <div className="max-w-lg mx-auto p-4 pt-6 space-y-5">
        <header className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-slate-400" data-testid="play-hello">Hoi {ticket.display_name}</div>
            <div className="text-sm font-bold text-slate-200 mt-0.5">{game?.title || "Bingo"}</div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-widest text-slate-500">Ronde</div>
            <div className="text-sm font-black text-[#FF007F]" data-testid="play-stage">{stage ? STAGE_LABELS[stage] : "-"}</div>
          </div>
        </header>

        <LiveStream url={game?.stream_url} compact />

        {pendingWin && (
          <PayoutForm win={pendingWin} defaultName={ticket.display_name} onDone={() => setPayoutDone((d) => [...d, pendingWin.claim_id])} />
        )}

        <div className="p-4 rounded-2xl bg-[#12141D] border border-white/10 flex items-center gap-4">
          <div className="scale-75 origin-left -my-4"><CurrentBall number={last} size="text-5xl" /></div>
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-400">Laatste nummer</div>
            <div className="text-3xl font-black text-[#FFD700]" data-testid="play-last">{last ?? "—"}</div>
            <div className="text-xs text-slate-500 mt-1">{game?.status === "paused" ? "⏸ Gepauzeerd" : game?.status === "live" ? "🔴 Live" : game ? "⏳ Wachten op start" : "⟳ Verbinden..."}</div>
            <div className="text-[10px] mt-0.5" data-testid="play-connection">
              {connected ? <span className="text-[#00E676]">● live verbonden</span> : polling ? <span className="text-[#FFD700]">● verbonden (ververst elke 2 s)</span> : <span className="text-[#FF1744]">● geen verbinding</span>}
            </div>
          </div>
          <div className="ml-auto text-right">
            <div className="text-xs uppercase tracking-widest text-slate-400">Getrokken</div>
            <div className="text-2xl font-black text-[#00F0FF]" data-testid="play-drawn-count">{drawn.length}</div>
          </div>
        </div>

        {tickets.length > 1 && (
          <div className="flex items-center gap-2" data-testid="card-switcher">
            <button data-testid="card-prev" onClick={() => setIdx((idx - 1 + tickets.length) % tickets.length)}
                    className="p-2 rounded-lg bg-[#12141D] border border-white/10 hover:bg-[#1F2833]"><ChevronLeft className="w-4 h-4" /></button>
            <div className="flex-1 flex gap-1.5 overflow-x-auto">
              {tickets.map((t, i) => {
                const ok = claimable.includes(i);
                return (
                  <button key={t.id} data-testid={`card-tab-${i}`} onClick={() => setIdx(i)}
                          className={`flex-1 min-w-[64px] py-2 rounded-lg text-xs font-black border transition ${
                            i === idx ? "bg-[#FFD700] text-[#0B0C10] border-[#FFD700]" : "bg-[#12141D] border-white/10 text-slate-300"
                          } ${ok && i !== idx ? "ring-2 ring-[#00E676] animate-pulse" : ""}`}>
                    Kaart {i + 1}
                  </button>
                );
              })}
            </div>
            <button data-testid="card-next" onClick={() => setIdx((idx + 1) % tickets.length)}
                    className="p-2 rounded-lg bg-[#12141D] border border-white/10 hover:bg-[#1F2833]"><ChevronRight className="w-4 h-4" /></button>
          </div>
        )}

        <motion.div key={current.id} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.25 }}
                    drag={tickets.length > 1 ? "x" : false} dragConstraints={{ left: 0, right: 0 }} dragElastic={0.2}
                    onDragEnd={(e, info) => {
                      if (info.offset.x < -70) setIdx((idx + 1) % tickets.length);
                      else if (info.offset.x > 70) setIdx((idx - 1 + tickets.length) % tickets.length);
                    }}>
          <BingoCard card={current.card} drawn={drawn} winRows={completeRows(current.card, drawn)} />
        </motion.div>

        <div className="flex justify-between items-center px-2">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500">Gemarkeerd</div>
            <div className="text-lg font-black" data-testid="play-marked">{stats.marked}/{stats.needed}</div>
          </div>
          {tickets.length > 1 && (
            <div className="text-center">
              <div className="text-xs uppercase tracking-widest text-slate-500">Kaart</div>
              <div className="text-lg font-black text-[#00F0FF]" data-testid="play-card-index">{idx + 1}/{tickets.length}</div>
            </div>
          )}
          <div className="text-right">
            <div className="text-xs uppercase tracking-widest text-slate-500">Volle rijen</div>
            <div className="text-lg font-black text-[#FFD700]" data-testid="play-rows-complete">{rc}/3</div>
          </div>
        </div>

        <div className="text-xs text-slate-500 px-2">
          Nummers worden automatisch afgestreept als de presentator ze trekt. {tickets.length > 1 ? "Veeg of tik om van kaart te wisselen. " : ""}Druk op BINGO! als je aan de winvoorwaarde voldoet.
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-[#0B0C10] via-[#0B0C10] to-transparent">
        <div className="max-w-lg mx-auto flex justify-center gap-3 mb-3" data-testid="reaction-bar">
          {Object.entries(REACTION_ICONS).map(([kind, { Icon, color, label }]) => (
            <button key={kind} data-testid={`react-${kind}`} onClick={() => sendReaction(kind)} aria-label={label}
                    className={`w-12 h-12 rounded-full border flex items-center justify-center transition-transform active:scale-90 ${burst === kind ? "scale-125" : ""}`}
                    style={{ borderColor: `${color}66`, background: `${color}1A`, color }}>
              <Icon className="w-5 h-5" fill={kind === "heart" ? color : "none"} />
            </button>
          ))}
        </div>
        <Button
          data-testid="claim-bingo-button"
          onClick={claim}
          disabled={!canClaim || claiming}
          className={`w-full h-16 text-2xl font-black rounded-2xl transition-all ${
            canClaim
              ? "bg-gradient-to-r from-[#FFD700] via-[#FF007F] to-[#00F0FF] text-[#0B0C10] shadow-[0_0_40px_rgba(255,215,0,0.6)] animate-pulse"
              : "bg-white/10 text-slate-500"
          }`}
          style={{ fontFamily: "Unbounded, sans-serif" }}
        >
          {claiming ? "..." : tickets.length > 1 ? `BINGO! (kaart ${idx + 1})` : "BINGO!"}
        </Button>
        {!canClaim && (
          <p className="text-center text-xs text-slate-500 mt-2" data-testid="claim-hint">
            {game?.pending_claim_id ? "Andere claim wordt beoordeeld..." :
             game?.status !== "live" ? "Wacht tot spel actief is..." :
             claimable.length ? `Kaart ${claimable[0] + 1} heeft bingo!` :
             stage === "row" ? "Complete 1 rij om bingo te claimen" :
             stage === "two_rows" ? "Complete 2 rijen om bingo te claimen" :
             "Complete de hele kaart voor volle bingo"}
          </p>
        )}
      </div>

      <AnimatePresence>
        {showWin && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            data-testid="win-overlay" onClick={() => setShowWin(null)}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-8 cursor-pointer">
            <motion.div initial={{ scale: 0.5 }} animate={{ scale: 1 }} className="text-center">
              <div className="text-6xl font-black bg-gradient-to-r from-[#FFD700] via-[#FF007F] to-[#00F0FF] bg-clip-text text-transparent"
                   style={{ fontFamily: "Unbounded, sans-serif" }}>
                {showWin.mine ? "JIJ WINT!" : "BINGO!"}
              </div>
              <div className="mt-3 text-xl text-slate-200">{showWin.name}</div>
              <div className="text-sm text-slate-400">{STAGE_LABELS[showWin.stage]}</div>
              <div className="mt-6 text-xs text-slate-500 uppercase tracking-widest">Tik om te sluiten</div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
